/* eslint-disable max-lines */
import { MapIconName } from '../../components/MapIcon';

export const WAYPOINTS_SIMPLIFIED_MINZOOM = 9;
export const WAYPOINTS_SIMPLIFIED_CIRCLE_RADIUS = 5;
export const WAYPOINTS_MINZOOM = 12;
export const WAYPOINTS_ICON_SIZE = 0.9;
export const SELECTED_WAYPOINT_ICON_SIZE = 1.1;

export type WaypointsToggleableGroup =
  | 'ANCHOR_SPOT'
  | 'CAMPFIRE'
  | 'DEFAULT'
  | 'ELECTRICITY'
  | 'FAVORITE'
  | 'FISH_SPOTTED'
  | 'GAS'
  | 'LILY_PADS'
  | 'LOST_FISH'
  | 'LOST_LURE'
  | 'NOPE'
  | 'NOTE'
  | 'PARKING'
  | 'PHOTO'
  | 'PICNIC'
  | 'ROCKS'
  | 'SCHOOL_OF_FISH'
  | 'SHELTER'
  | 'SLIPWAY'
  | 'STRUCTURE'
  | 'SUNKEN_TREE'
  | 'VEGETATION'
  | 'VILE_AREA'
  | 'WARNING'
  | 'WRECK';

export enum WaypointCategories {
  FISHING = 'Fishing',
  AMENITIES = 'Amenities',
  NOTEWORTHY = 'Noteworthy',
}

export interface Waypoint {
  name: string;
  layerId: string;
  typeId: string;
  icon: MapIconName;
  background: string;
  category: WaypointCategories;
  order: number;
  toggleableGroup: WaypointsToggleableGroup;
}

export interface Waypoints {
  ANCHOR_SPOT: Waypoint;
  CAMPFIRE: Waypoint;
  DEFAULT: Waypoint;
  ELECTRICITY: Waypoint;
  FAVORITE: Waypoint;
  FISH_SPOTTED: Waypoint;
  GAS: Waypoint;
  LILY_PADS: Waypoint;
  LOST_FISH: Waypoint;
  LOST_LURE: Waypoint;
  NOPE: Waypoint;
  NOTE: Waypoint;
  PARKING: Waypoint;
  PHOTO: Waypoint;
  PICNIC: Waypoint;
  ROCKS: Waypoint;
  SCHOOL_OF_FISH: Waypoint;
  SHELTER: Waypoint;
  SLIPWAY: Waypoint;
  STRUCTURE: Waypoint;
  SUNKEN_TREE: Waypoint;
  VEGETATION: Waypoint;
  VILE_AREA: Waypoint;
  WARNING: Waypoint;
  WRECK: Waypoint;
}

export const WAYPOINTS: Waypoints = {
  FAVORITE: {
    name: 'Great spot',
    layerId: 'waypoints_favorite_layer',
    icon: 'waypoint_favorite',
    background: '#FFAA00',
    typeId: 'star',
    category: WaypointCategories.FISHING,
    order: 1,
    toggleableGroup: 'FAVORITE',
  },
  SCHOOL_OF_FISH: {
    name: 'School of fish',
    layerId: 'waypoints_school_of_fish_layer',
    typeId: 'school-of-fish',
    icon: 'waypoint_school_of_fish',
    background: '#09467F',
    category: WaypointCategories.FISHING,
    order: 2,
    toggleableGroup: 'SCHOOL_OF_FISH',
  },
  FISH_SPOTTED: {
    name: 'Big fish spotted',
    layerId: 'waypoints_fish_spotted_layer',
    typeId: 'fish',
    icon: 'waypoint_fish_spotted',
    background: '#09467F',
    category: WaypointCategories.FISHING,
    order: 3,
    toggleableGroup: 'FISH_SPOTTED',
  },
  LOST_FISH: {
    name: 'Lost a big one',
    layerId: 'waypoints_lost_fish_layer',
    typeId: 'cross',
    icon: 'waypoint_lost_fish',
    background: '#943000',
    category: WaypointCategories.FISHING,
    order: 4,
    toggleableGroup: 'LOST_FISH',
  },
  LOST_LURE: {
    name: 'Lost a lure',
    layerId: 'waypoints_lost_lure_layer',
    typeId: 'lure',
    icon: 'waypoint_lost_lure',
    background: '#943000',
    category: WaypointCategories.FISHING,
    order: 5,
    toggleableGroup: 'LOST_LURE',
  },
  VEGETATION: {
    name: 'Grass',
    layerId: 'waypoints_vegetation_layer',
    typeId: 'vegetation',
    icon: 'waypoint_vegetation',
    background: '#109035',
    category: WaypointCategories.FISHING,
    order: 6,
    toggleableGroup: 'VEGETATION',
  },
  LILY_PADS: {
    name: 'Lily pads',
    layerId: 'waypoints_lily_pads_layer',
    typeId: 'lily-pad',
    icon: 'waypoint_lillypads',
    background: '#109035',
    category: WaypointCategories.FISHING,
    order: 7,
    toggleableGroup: 'LILY_PADS',
  },
  ROCKS: {
    name: 'Rocks',
    layerId: 'waypoints_rocks_layer',
    typeId: 'rocks',
    icon: 'waypoint_rocks',
    background: '#836651',
    category: WaypointCategories.FISHING,
    order: 8,
    toggleableGroup: 'ROCKS',
  },
  SUNKEN_TREE: {
    name: 'Sunken tree',
    layerId: 'waypoints_sunken_tree_layer',
    typeId: 'log',
    icon: 'waypoint_sunken_tree',
    background: '#836651',
    category: WaypointCategories.FISHING,
    order: 9,
    toggleableGroup: 'SUNKEN_TREE',
  },
  WRECK: {
    name: 'Wreck',
    layerId: 'waypoints_wreck_layer',
    typeId: 'wreck',
    icon: 'waypoint_wreck',
    background: '#836651',
    category: WaypointCategories.FISHING,
    order: 10,
    toggleableGroup: 'WRECK',
  },
  STRUCTURE: {
    name: 'Structure',
    layerId: 'waypoints_structure_layer',
    typeId: 'structure',
    icon: 'waypoint_structure',
    background: '#836651',
    category: WaypointCategories.FISHING,
    order: 11,
    toggleableGroup: 'STRUCTURE',
  },
  ANCHOR_SPOT: {
    name: 'Anchor spot',
    layerId: 'waypoints_anchor_spot_layer',
    typeId: 'anchor',
    icon: 'waypoint_anchor_spot',
    background: '#836651',
    category: WaypointCategories.FISHING,
    order: 12,
    toggleableGroup: 'ANCHOR_SPOT',
  },
  SLIPWAY: {
    name: 'Slipway',
    layerId: 'waypoints_slipway_layer',
    typeId: 'slipway',
    icon: 'waypoint_slipway',
    background: '#1364BE',
    category: WaypointCategories.AMENITIES,
    order: 13,
    toggleableGroup: 'SLIPWAY',
  },
  PARKING: {
    name: 'Parking',
    layerId: 'waypoints_parking_layer',
    typeId: 'parking',
    icon: 'waypoint_parking',
    background: '#1364BE',
    category: WaypointCategories.AMENITIES,
    order: 14,
    toggleableGroup: 'PARKING',
  },
  SHELTER: {
    name: 'Shelter',
    layerId: 'waypoints_shelter_layer',
    typeId: 'shelter',
    icon: 'waypoint_shelter',
    background: '#5F733A',
    category: WaypointCategories.AMENITIES,
    order: 15,
    toggleableGroup: 'SHELTER',
  },
  PICNIC: {
    name: 'Picnic area',
    layerId: 'waypoints_picnic_layer',
    typeId: 'table',
    icon: 'waypoint_picnic',
    background: '#5F733A',
    category: WaypointCategories.AMENITIES,
    order: 16,
    toggleableGroup: 'PICNIC',
  },
  CAMPFIRE: {
    name: 'Bonfire area',
    layerId: 'waypoints_campfire_layer',
    typeId: 'campfire',
    icon: 'waypoint_campfire',
    background: '#5F733A',
    category: WaypointCategories.AMENITIES,
    order: 17,
    toggleableGroup: 'CAMPFIRE',
  },
  GAS: {
    name: 'Gas',
    layerId: 'waypoints_gas_layer',
    typeId: 'fuel',
    icon: 'waypoint_gas',
    background: '#FFB800',
    category: WaypointCategories.AMENITIES,
    order: 18,
    toggleableGroup: 'GAS',
  },
  ELECTRICITY: {
    name: 'Electricity',
    layerId: 'waypoints_electricity_layer',
    typeId: 'bolt',
    icon: 'waypoint_electricity',
    background: '#FFB800',
    category: WaypointCategories.AMENITIES,
    order: 19,
    toggleableGroup: 'ELECTRICITY',
  },
  DEFAULT: {
    name: 'Marker',
    layerId: 'waypoints_default_layer',
    typeId: 'default',
    icon: 'waypoint_default',
    background: '#00A89F',
    category: WaypointCategories.NOTEWORTHY,
    order: 20,
    toggleableGroup: 'DEFAULT',
  },
  NOTE: {
    name: 'Note',
    layerId: 'waypoints_note_layer',
    typeId: 'pencil',
    icon: 'waypoint_note',
    background: '#D8D8D8',
    category: WaypointCategories.NOTEWORTHY,
    order: 21,
    toggleableGroup: 'NOTE',
  },
  PHOTO: {
    name: 'Photo',
    layerId: 'waypoints_photo_layer',
    typeId: 'camera',
    icon: 'waypoint_photo',
    background: '#D8D8D8',
    category: WaypointCategories.NOTEWORTHY,
    order: 22,
    toggleableGroup: 'PHOTO',
  },
  WARNING: {
    name: 'Obstacle',
    layerId: 'waypoints_warning_layer',
    typeId: 'warning',
    icon: 'waypoint_warning',
    background: '#FF0700',
    category: WaypointCategories.NOTEWORTHY,
    order: 23,
    toggleableGroup: 'WARNING',
  },
  NOPE: {
    name: 'Nope',
    layerId: 'waypoints_nope_layer',
    typeId: 'skull',
    icon: 'waypoint_nope',
    background: '#121212',
    category: WaypointCategories.NOTEWORTHY,
    order: 24,
    toggleableGroup: 'NOPE',
  },
  VILE_AREA: {
    name: 'Vile area',
    layerId: 'waypoints_vile_area_layer',
    typeId: 'gator',
    icon: 'waypoint_vile_area',
    background: '#121212',
    category: WaypointCategories.NOTEWORTHY,
    order: 25,
    toggleableGroup: 'VILE_AREA',
  },
};

export const ALL_WAYPOINTS_TOGGLEABLE_GROUPS: WaypointsToggleableGroup[] = Object.values(
  WAYPOINTS,
).map(({ toggleableGroup }) => toggleableGroup);
